import { zodResolver } from '@hookform/resolvers/zod';
import type { AdminSelf } from '@saay/api-client';
import { formatCount, normalizeSearchText, toPersianDigits } from '@saay/formatting';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import type { PhysicalCardProvinceUsage, PhysicalCardSeriesUsage } from '../api.js';
import { QueryState } from '../components/guard.js';
import { ListSearchInput } from '../components/list-filters.js';
import { SurfaceSection } from '../components/surface-section.js';
import {
  Banner,
  Button,
  Checkbox,
  EmptyState,
  IconButton,
  Menu,
  MenuItem,
  NativeSelect,
  SegmentedButtons,
  Skeleton,
  StatusChip,
  Table,
  TextField,
} from '../kit.js';
import { physicalCardsPath } from '../nav.js';
import { canManageNumberingPolicy } from '../permissions.js';
import { useAllNumberingCities, useNumberingUsage, useProvinces } from '../queries.js';
import {
  activateSeries,
  addSeries,
  defaultReason,
  digitsOnly,
  dropProvince,
  dropSeries,
  EDIT_PARAM,
  editTargetOf,
  NUMBERING_BOUNDS,
  nextProvinceDigits,
  prefixProblem,
  putProvince,
  qrLinkBaseProblem,
  REASON_PRESETS,
  setQrLinkBase,
  valuesOf,
} from './physical-card-config.js';
import { Digits, NumberLayout, versionTitle, WIDTH_LABEL } from './physical-card-numbering.js';
import {
  ChangeDialog,
  ProblemLine,
  ReasonPicker,
  reasonProblem,
  useNumberingSave,
  useNumberingVersion,
} from './physical-card-save.js';

/**
 * «تنظیمات شماره‌گذاری» (D-117, AC §31.6): the picture of the 16 digits, then two small tables, «سری‌ها»
 * and «استان‌ها», with their row actions for the Super Admin (add, activate, edit, delete), each in a
 * small dialog that posts the whole configuration with the one change. Readers see the same tables
 * without actions. Fields a card already uses are read-only, with a hint; the server's lock refusals
 * still show in the dialog.
 */

type SettingsDialog =
  | { readonly kind: 'add-series'; readonly activate: boolean }
  | { readonly kind: 'activate'; readonly prefix?: string | undefined }
  | { readonly kind: 'drop-series'; readonly prefix: string }
  | { readonly kind: 'province'; readonly row?: PhysicalCardProvinceUsage | undefined }
  | { readonly kind: 'drop-province'; readonly row: PhysicalCardProvinceUsage }
  | { readonly kind: 'qr-link' };

export function NumberingSettingsPanel({ self }: { readonly self: AdminSelf }) {
  const manage = canManageNumberingPolicy(self);
  const [params, setParams] = useSearchParams();
  const { query: current, version } = useNumberingVersion();
  const usage = useNumberingUsage();
  const [dialog, setDialog] = useState<SettingsDialog | undefined>(() => {
    if (!manage) return undefined;
    const target = editTargetOf(params.get(EDIT_PARAM));
    if (target === 'series') return { activate: true, kind: 'add-series' };
    if (target === 'activate') return { kind: 'activate' };
    return undefined;
  });

  useEffect(() => {
    if (!params.has(EDIT_PARAM)) return;
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(EDIT_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [params, setParams]);

  const failed = current.status === 'error' ? current : usage.status === 'error' ? usage : null;
  const close = () => setDialog(undefined);
  return (
    <div className="flex flex-col gap-4">
      <QueryState
        error={failed?.error}
        onRetry={() => {
          void current.refetch();
          void usage.refetch();
        }}
        skeleton={<Skeleton label="در حال بارگذاری نسخه جاری…" lines={6} />}
        status={
          failed !== null
            ? 'error'
            : current.status === 'success' && usage.status === 'success'
              ? 'success'
              : 'pending'
        }
      >
        {version === undefined || usage.data === undefined ? null : (
          <>
            <p className="text-sm text-muted-foreground">{versionTitle(version)}</p>
            <NumberLayout />
            <SeriesSection
              activePrefix={version.activeSeriesPrefix}
              manage={manage}
              onDialog={setDialog}
              rows={usage.data.series}
            />
            <ProvincesSection manage={manage} onDialog={setDialog} rows={usage.data.provinces} />
            <QrLinkSection base={version.qrLinkBase ?? null} manage={manage} onDialog={setDialog} />
          </>
        )}
      </QueryState>
      {manage && dialog !== undefined ? <SettingsDialogs dialog={dialog} onClose={close} /> : null}
    </div>
  );
}

function SettingsDialogs({
  dialog,
  onClose,
}: {
  readonly dialog: SettingsDialog;
  readonly onClose: () => void;
}) {
  switch (dialog.kind) {
    case 'add-series':
      return <AddSeriesDialog activate={dialog.activate} onClose={onClose} />;
    case 'activate':
      return <ActivateSeriesDialog onClose={onClose} prefix={dialog.prefix} />;
    case 'drop-series':
      return <DropSeriesDialog onClose={onClose} prefix={dialog.prefix} />;
    case 'province':
      return <ProvinceDialog onClose={onClose} row={dialog.row} />;
    case 'drop-province':
      return <DropProvinceDialog onClose={onClose} row={dialog.row} />;
    case 'qr-link':
      return <QrLinkDialog onClose={onClose} />;
  }
}

// ---------------------------------------------------------------------------------------------
// «سری‌ها»

function SeriesSection({
  activePrefix,
  manage,
  onDialog,
  rows,
}: {
  readonly activePrefix: string | null;
  readonly manage: boolean;
  readonly onDialog: (dialog: SettingsDialog) => void;
  readonly rows: readonly PhysicalCardSeriesUsage[];
}) {
  const full = rows.length >= NUMBERING_BOUNDS.series;
  return (
    <SurfaceSection
      {...(manage
        ? {
            action: (
              <Button
                disabled={full}
                icon="plus"
                onClick={() => onDialog({ activate: activePrefix === null, kind: 'add-series' })}
                size="sm"
                title={full ? 'حداکثر ۲۰ سری' : undefined}
              >
                افزودن سری
              </Button>
            ),
          }
        : {})}
      description="کارت‌های جدید از سری فعال شماره می‌گیرند؛ شماره کارت‌های قبلی عوض نمی‌شود."
      id="physical-card-settings-series"
      title="سری‌ها"
    >
      {rows.length === 0 ? (
        <EmptyState
          {...(manage
            ? {
                action: (
                  <Button
                    icon="plus"
                    onClick={() => onDialog({ activate: true, kind: 'add-series' })}
                    size="sm"
                  >
                    افزودن سری
                  </Button>
                ),
              }
            : {})}
          description="تا یک سری ساخته و فعال نشود، کارتی صادر نمی‌شود."
          title="هنوز سری‌ای تعریف نشده"
        />
      ) : (
        <>
          {activePrefix === null ? (
            <Banner tone="warning">
              <span className="flex flex-wrap items-center justify-between gap-2">
                <span>هیچ سری فعال نیست؛ کارتی صادر نمی‌شود.</span>
                {manage ? (
                  <Button
                    onClick={() =>
                      onDialog({
                        kind: 'activate',
                        prefix: rows.length === 1 ? rows[0]?.prefix : undefined,
                      })
                    }
                    size="sm"
                  >
                    فعال کردن
                  </Button>
                ) : null}
              </span>
            </Banner>
          ) : null}
          <Table
            caption="سری‌ها"
            columns={[
              {
                header: 'پیشوند',
                key: 'prefix',
                pin: 'start',
                render: (row: PhysicalCardSeriesUsage) => (
                  <span className="font-bold tabular-nums">
                    <Digits value={row.prefix} />
                  </span>
                ),
              },
              {
                header: 'وضعیت',
                key: 'active',
                render: (row) =>
                  row.active ? (
                    <StatusChip title="فعال" tone="success" />
                  ) : (
                    <StatusChip title="غیرفعال" tone="neutral" />
                  ),
              },
              {
                header: 'کارت صادرشده',
                key: 'assigned',
                numeric: true,
                render: (row) => formatCount(row.assignedCount),
              },
              ...(manage
                ? [
                    {
                      detail: false,
                      header: <span className="sr-only">کارها</span>,
                      key: 'actions',
                      pin: 'end' as const,
                      render: (row: PhysicalCardSeriesUsage) => (
                        <Menu
                          label={`کارهای سری ${toPersianDigits(row.prefix)}`}
                          trigger={
                            <IconButton
                              icon="more"
                              label={`کارهای سری ${toPersianDigits(row.prefix)}`}
                            />
                          }
                        >
                          <MenuItem
                            disabled={row.active}
                            disabledReason="همین حالا فعال است."
                            onSelect={() => onDialog({ kind: 'activate', prefix: row.prefix })}
                          >
                            فعال کردن
                          </MenuItem>
                          <MenuItem
                            destructive
                            disabled={row.locked || row.active}
                            disabledReason={
                              row.locked
                                ? 'با این سری کارت صادر شده؛ برای همیشه می‌ماند.'
                                : 'اول سری دیگری را فعال کنید.'
                            }
                            onSelect={() => onDialog({ kind: 'drop-series', prefix: row.prefix })}
                          >
                            حذف
                          </MenuItem>
                        </Menu>
                      ),
                    },
                  ]
                : []),
            ]}
            rowKey={(row) => row.prefix}
            rows={rows}
          />
        </>
      )}
    </SurfaceSection>
  );
}

const seriesSchema = z.object({ activate: z.boolean(), prefix: z.string(), reason: z.string() });

function AddSeriesDialog({
  activate,
  onClose,
}: {
  readonly activate: boolean;
  readonly onClose: () => void;
}) {
  const save = useNumberingSave('series-add', onClose);
  const form = useForm<z.infer<typeof seriesSchema>>({
    defaultValues: {
      activate,
      prefix: '',
      reason: defaultReason('addSeries'),
    },
    resolver: zodResolver(seriesSchema),
  });
  const errors = form.formState.errors;

  async function submit(values: z.infer<typeof seriesSchema>) {
    if (save.version === undefined) return;
    const prefixError = prefixProblem(values.prefix, valuesOf(save.version));
    const reasonError = reasonProblem(values.reason);
    if (prefixError !== undefined) form.setError('prefix', { message: prefixError });
    if (reasonError !== undefined) form.setError('reason', { message: reasonError });
    if (prefixError !== undefined || reasonError !== undefined) return;
    await save.save({
      change: addSeries(values.prefix, values.activate),
      fields: (next) => ({
        [`series.${next.series.length - 1}.prefix`]: 'prefix',
      }),
      reason: values.reason,
      setError: (field, message) => form.setError(field as 'prefix', { message, type: 'server' }),
    });
  }

  return (
    <ChangeDialog
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSubmit={() => void form.handleSubmit(submit)()}
      open
      reason={
        <ReasonPicker
          error={errors.reason?.message}
          onChange={(value) => form.setValue('reason', value, { shouldValidate: true })}
          presets={REASON_PRESETS.addSeries}
          value={form.watch('reason')}
        />
      }
      save={save}
      title="افزودن سری"
    >
      <TextField
        error={errors.prefix?.message}
        hint="شش رقم، همان پیشوندی که صادرکننده داده"
        inputMode="numeric"
        label="پیشوند سری"
        ltr
        maxLength={6}
        {...form.register('prefix', { setValueAs: digitsOnly })}
      />
      <Banner tone="warning">
        وقتی اولین کارت با این سری صادر شود، پیشوند برای همیشه قفل می‌شود و دیگر حذف نمی‌شود.
      </Banner>
      <Checkbox
        label="کارت‌های جدید از همین حالا با این سری صادر شود"
        {...form.register('activate')}
      />
    </ChangeDialog>
  );
}

function ActivateSeriesDialog({
  onClose,
  prefix,
}: {
  readonly onClose: () => void;
  readonly prefix: string | undefined;
}) {
  const save = useNumberingSave('series-activate', onClose);
  const options = (save.version?.series ?? []).filter(
    (row) => row.prefix !== save.version?.activeSeriesPrefix,
  );
  const [chosen, setChosen] = useState(prefix ?? '');
  const [reason, setReason] = useState(defaultReason('activateSeries'));
  const [errors, setErrors] = useState<{
    readonly prefix?: string | undefined;
    readonly reason?: string | undefined;
  }>({});
  const selected = chosen !== '' ? chosen : options.length === 1 ? (options[0]?.prefix ?? '') : '';
  return (
    <ChangeDialog
      description={
        selected === ''
          ? 'کارت‌های جدید با سری انتخاب‌شده صادر می‌شوند؛ شماره کارت‌های قبلی عوض نمی‌شود.'
          : `از این به بعد کارت‌های جدید با سری ${toPersianDigits(selected)} صادر می‌شوند؛ شماره کارت‌های قبلی عوض نمی‌شود.`
      }
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSubmit={() => {
        const next = {
          ...(selected === '' ? { prefix: 'یک سری را انتخاب کنید.' } : {}),
          ...(reasonProblem(reason) === undefined ? {} : { reason: reasonProblem(reason) }),
        };
        setErrors(next);
        if (Object.keys(next).length === 0) {
          void save.save({ change: activateSeries(selected), reason });
        }
      }}
      open
      reason={
        <ReasonPicker
          error={errors.reason}
          onChange={setReason}
          presets={REASON_PRESETS.activateSeries}
          value={reason}
        />
      }
      save={save}
      submitLabel="فعال کردن"
      title={prefix === undefined ? 'فعال کردن سری' : `فعال کردن سری ${toPersianDigits(prefix)}`}
    >
      {prefix === undefined ? (
        <NativeSelect
          error={errors.prefix}
          label="سری"
          onChange={(event) => setChosen(event.target.value)}
          options={options.map((row) => ({
            label: toPersianDigits(row.prefix),
            value: row.prefix,
          }))}
          placeholder="انتخاب سری"
          value={selected}
        />
      ) : null}
    </ChangeDialog>
  );
}

function DropSeriesDialog({
  onClose,
  prefix,
}: {
  readonly onClose: () => void;
  readonly prefix: string;
}) {
  const save = useNumberingSave('series-drop', onClose);
  const [reason, setReason] = useState(defaultReason('dropSeries'));
  const [error, setError] = useState<string | undefined>();
  return (
    <ChangeDialog
      description="با این سری کارتی صادر نشده؛ حذفش چیزی را عوض نمی‌کند."
      destructive
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSubmit={() => {
        const problem = reasonProblem(reason);
        setError(problem);
        if (problem === undefined) void save.save({ change: dropSeries(prefix), reason });
      }}
      open
      reason={
        <ReasonPicker
          error={error}
          onChange={setReason}
          presets={REASON_PRESETS.dropSeries}
          value={reason}
        />
      }
      save={save}
      submitLabel="حذف سری"
      title={`حذف سری ${toPersianDigits(prefix)}`}
    />
  );
}

// ---------------------------------------------------------------------------------------------
// «استان‌ها»

function ProvincesSection({
  manage,
  onDialog,
  rows,
}: {
  readonly manage: boolean;
  readonly onDialog: (dialog: SettingsDialog) => void;
  readonly rows: readonly PhysicalCardProvinceUsage[];
}) {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [query, setQuery] = useState('');
  const needle = normalizeSearchText(query.trim());
  const shown = rows.filter(
    (row) => needle === '' || normalizeSearchText(row.provinceNameFa).includes(needle),
  );

  function citiesOf(provinceId: string) {
    const params = new URLSearchParams(search);
    params.delete(EDIT_PARAM);
    params.set('province', provinceId);
    void navigate({ pathname: physicalCardsPath('cities'), search: `?${params.toString()}` });
  }

  return (
    <SurfaceSection
      {...(manage
        ? {
            action: (
              <Button
                disabled={rows.length >= NUMBERING_BOUNDS.provinces}
                icon="plus"
                onClick={() => onDialog({ kind: 'province' })}
                size="sm"
              >
                افزودن استان
              </Button>
            ),
          }
        : {})}
      description="کد هر شهر با رقم کشور و رقم استانش شروع می‌شود."
      id="physical-card-settings-provinces"
      title="استان‌ها"
    >
      {rows.length === 0 ? (
        <EmptyState title="هنوز استانی کد ندارد" />
      ) : (
        <>
          <ListSearchInput
            label="جست‌وجوی استان"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="نام استان"
            value={query}
          />
          {shown.length === 0 ? (
            <EmptyState icon="search" title="استانی با این نام پیدا نشد" />
          ) : (
            <Table
              caption="کد استان‌ها"
              columns={[
                {
                  header: 'استان',
                  key: 'province',
                  pin: 'start',
                  render: (row: PhysicalCardProvinceUsage) => row.provinceNameFa,
                },
                {
                  header: 'رقم کشور',
                  key: 'country',
                  render: (row) => <Digits value={String(row.countryDigit)} />,
                },
                {
                  header: 'رقم استان',
                  key: 'digit',
                  render: (row) => <Digits value={String(row.provinceDigit)} />,
                },
                {
                  header: 'نوع کد شهرها',
                  key: 'width',
                  render: (row) => WIDTH_LABEL[row.cityCodeWidth],
                },
                {
                  header: 'تعداد شهر',
                  key: 'cities',
                  numeric: true,
                  render: (row) => formatCount(row.cityCount),
                },
                {
                  header: 'کارت فعال',
                  key: 'live',
                  numeric: true,
                  render: (row) => formatCount(row.liveCount),
                },
                ...(manage
                  ? [
                      {
                        detail: false,
                        header: <span className="sr-only">کارها</span>,
                        key: 'actions',
                        pin: 'end' as const,
                        render: (row: PhysicalCardProvinceUsage) => (
                          <Menu
                            label={`کارهای ${row.provinceNameFa}`}
                            trigger={
                              <IconButton icon="more" label={`کارهای ${row.provinceNameFa}`} />
                            }
                          >
                            <MenuItem onSelect={() => onDialog({ kind: 'province', row })}>
                              ویرایش
                            </MenuItem>
                            <MenuItem onSelect={() => citiesOf(row.provinceId)}>
                              شهرهای این استان
                            </MenuItem>
                            <MenuItem
                              destructive
                              disabled={row.locked}
                              disabledReason="در این استان کارت صادر شده؛ حذف نمی‌شود."
                              onSelect={() => onDialog({ kind: 'drop-province', row })}
                            >
                              حذف
                            </MenuItem>
                          </Menu>
                        ),
                      },
                    ]
                  : [
                      {
                        detail: false,
                        header: <span className="sr-only">شهرها</span>,
                        key: 'cities-link',
                        pin: 'end' as const,
                        render: (row: PhysicalCardProvinceUsage) => (
                          <Button
                            aria-label={`شهرهای ${row.provinceNameFa}`}
                            onClick={() => citiesOf(row.provinceId)}
                            size="sm"
                            variant="ghost"
                          >
                            شهرها
                          </Button>
                        ),
                      },
                    ]),
              ]}
              rowKey={(row) => row.provinceId}
              rows={shown}
            />
          )}
        </>
      )}
    </SurfaceSection>
  );
}

const provinceSchema = z.object({
  cityCodeWidth: z.enum(['1', '2']),
  countryDigit: z.string().regex(/^[0-9]$/, 'یک رقم از ۰ تا ۹.'),
  provinceDigit: z.string().regex(/^[0-9]$/, 'یک رقم از ۰ تا ۹.'),
  provinceId: z.string().min(1, 'استان را انتخاب کنید.'),
  reason: z.string(),
});

type ProvinceForm = z.infer<typeof provinceSchema>;

const WIDTH_HINT: Readonly<Record<'1' | '2', string>> = {
  1: 'کد هر شهر یک رقم است (۰ تا ۹)؛ تا ۱۰ شهر جا می‌شود و کد کامل سه‌رقمی است.',
  2: 'کد هر شهر دو رقم است (۰۰ تا ۹۹)؛ تا ۱۰۰ شهر جا می‌شود و کد کامل چهاررقمی است.',
};

function ProvinceDialog({
  onClose,
  row,
}: {
  readonly onClose: () => void;
  readonly row: PhysicalCardProvinceUsage | undefined;
}) {
  const save = useNumberingSave(row === undefined ? 'province-add' : 'province-edit', onClose);
  const geography = useProvinces();
  const values = save.version === undefined ? undefined : valuesOf(save.version);
  const suggestion = values === undefined ? undefined : nextProvinceDigits(values.provinces);
  const form = useForm<ProvinceForm>({
    defaultValues: {
      cityCodeWidth: row?.cityCodeWidth === 2 ? '2' : '1',
      countryDigit: row === undefined ? '' : String(row.countryDigit),
      provinceDigit: row === undefined ? '' : String(row.provinceDigit),
      provinceId: row?.provinceId ?? '',
      reason: defaultReason(row === undefined ? 'addProvince' : 'editProvince'),
    },
    resolver: zodResolver(provinceSchema),
  });
  const [typed, setTyped] = useState(false);
  useEffect(() => {
    if (row !== undefined || typed || suggestion === undefined) return;
    form.setValue('countryDigit', String(suggestion.countryDigit));
    form.setValue('provinceDigit', String(suggestion.provinceDigit));
  }, [form, row, suggestion?.countryDigit, suggestion?.provinceDigit, typed]);

  const errors = form.formState.errors;
  const width = form.watch('cityCodeWidth');
  const locked = row?.locked === true;
  /** Cities with codes of the old width would no longer fit: the width waits until they go. */
  const widthFixed = locked || (row !== undefined && row.cityCount > 0);
  const options = useMemo(
    () =>
      (geography.data ?? [])
        .filter((item) => !values?.provinces.some((p) => p.provinceId === item.id))
        .sort((left, right) => left.nameFa.localeCompare(right.nameFa, 'fa'))
        .map((item) => ({ label: item.nameFa, value: item.id })),
    [geography.data, values],
  );

  async function submit(data: ProvinceForm) {
    if (values === undefined) return;
    const reasonError = reasonProblem(data.reason);
    const clash = values.provinces.some(
      (item) =>
        item.provinceId !== data.provinceId &&
        item.countryDigit === Number(data.countryDigit) &&
        item.provinceDigit === Number(data.provinceDigit),
    );
    if (clash) form.setError('provinceDigit', { message: 'این کد استان را استان دیگری دارد.' });
    if (reasonError !== undefined) form.setError('reason', { message: reasonError });
    if (clash || reasonError !== undefined) return;
    await save.save({
      change: putProvince({
        cityCodeWidth: data.cityCodeWidth === '2' ? 2 : 1,
        countryDigit: Number(data.countryDigit),
        provinceDigit: Number(data.provinceDigit),
        provinceId: data.provinceId,
      }),
      fields: (next) => {
        const at = next.provinces.findIndex((item) => item.provinceId === data.provinceId);
        return {
          [`provinces.${at}.cityCodeWidth`]: 'cityCodeWidth',
          [`provinces.${at}.countryDigit`]: 'countryDigit',
          [`provinces.${at}.provinceDigit`]: 'provinceDigit',
          [`provinces.${at}.provinceId`]: 'provinceId',
        };
      },
      reason: data.reason,
      setError: (field, message) =>
        form.setError(field as keyof ProvinceForm, { message, type: 'server' }),
    });
  }

  return (
    <ChangeDialog
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSubmit={() => void form.handleSubmit(submit)()}
      open
      reason={
        <ReasonPicker
          error={errors.reason?.message}
          onChange={(value) => form.setValue('reason', value, { shouldValidate: true })}
          presets={REASON_PRESETS[row === undefined ? 'addProvince' : 'editProvince']}
          value={form.watch('reason')}
        />
      }
      save={save}
      title={row === undefined ? 'افزودن استان' : `ویرایش ${row.provinceNameFa}`}
    >
      {row === undefined ? (
        <NativeSelect
          error={
            errors.provinceId?.message ??
            (geography.status === 'error'
              ? 'فهرست استان‌ها باز نشد؛ پنجره را ببندید و دوباره باز کنید.'
              : undefined)
          }
          label="استان"
          options={options}
          placeholder="انتخاب استان"
          {...form.register('provinceId')}
        />
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <TextField
          error={errors.countryDigit?.message}
          hint="برای ایران ۰"
          inputMode="numeric"
          label="رقم کشور"
          ltr
          maxLength={1}
          readOnly={locked}
          {...form.register('countryDigit', {
            onChange: () => setTyped(true),
            setValueAs: digitsOnly,
          })}
        />
        <TextField
          error={errors.provinceDigit?.message}
          inputMode="numeric"
          label="رقم استان"
          ltr
          maxLength={1}
          readOnly={locked}
          {...form.register('provinceDigit', {
            onChange: () => setTyped(true),
            setValueAs: digitsOnly,
          })}
        />
      </div>
      {widthFixed ? (
        <p className="text-sm">
          <span className="text-muted-foreground">نوع کد شهرها: </span>
          {WIDTH_LABEL[width === '2' ? 2 : 1]}
        </p>
      ) : (
        <SegmentedButtons
          label="نوع کد شهرها"
          onChange={(key) => form.setValue('cityCodeWidth', key === '2' ? '2' : '1')}
          options={[
            { key: '1', label: WIDTH_LABEL[1] },
            { key: '2', label: WIDTH_LABEL[2] },
          ]}
          value={width}
        />
      )}
      <p className="text-xs text-muted-foreground">
        {locked
          ? 'در این استان کارت صادر شده؛ رقم‌ها و نوع کد شهرها ثابت می‌ماند.'
          : widthFixed
            ? 'این استان شهر دارد؛ نوع کد شهرها تا حذف شهرهایش ثابت می‌ماند.'
            : WIDTH_HINT[width]}
      </p>
    </ChangeDialog>
  );
}

function DropProvinceDialog({
  onClose,
  row,
}: {
  readonly onClose: () => void;
  readonly row: PhysicalCardProvinceUsage;
}) {
  const save = useNumberingSave('province-drop', onClose);
  const cities = useAllNumberingCities();
  const cityIds = (cities.data ?? [])
    .filter((city) => city.provinceId === row.provinceId)
    .map((city) => city.cityId);
  const [reason, setReason] = useState(defaultReason('dropProvince'));
  const [error, setError] = useState<string | undefined>();
  const [waiting, setWaiting] = useState(false);
  return (
    <ChangeDialog
      description={
        row.cityCount === 0
          ? `کد استان ${row.provinceNameFa} آزاد می‌شود.`
          : `کد استان ${row.provinceNameFa} و ${formatCount(row.cityCount)} شهر آن حذف می‌شود.`
      }
      destructive
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSubmit={() => {
        const problem = reasonProblem(reason);
        setError(problem);
        // The cities go with the province, so their list must be known first.
        const known = row.cityCount === 0 || cities.data !== undefined;
        setWaiting(!known);
        if (problem === undefined && known) {
          void save.save({ change: dropProvince(row.provinceId, cityIds), reason });
        }
      }}
      open
      reason={
        <ReasonPicker
          error={error}
          onChange={setReason}
          presets={REASON_PRESETS.dropProvince}
          value={reason}
        />
      }
      save={save}
      submitLabel="حذف استان"
      title={`حذف ${row.provinceNameFa}`}
    >
      <ProblemLine
        message={waiting ? 'فهرست شهرهای استان هنوز خوانده نشده؛ کمی بعد دوباره بزنید.' : undefined}
      />
    </ChangeDialog>
  );
}

// ---------------------------------------------------------------------------------------------
// «لینک QR کارت» (D-119, AC §33.4)

function QrLinkSection({
  base,
  manage,
  onDialog,
}: {
  readonly base: string | null;
  readonly manage: boolean;
  readonly onDialog: (dialog: SettingsDialog) => void;
}) {
  return (
    <SurfaceSection
      {...(manage
        ? {
            action: (
              <Button icon="edit" onClick={() => onDialog({ kind: 'qr-link' })} size="sm">
                ویرایش
              </Button>
            ),
          }
        : {})}
      description="QR کارت این لینک است. کارت‌هایی که قبلاً چاپ شده‌اند با لینک قبلی هم کار می‌کنند."
      id="physical-card-settings-qr-link"
      title="لینک QR کارت"
    >
      {base === null ? (
        <p className="text-sm">آدرس پیش‌فرض (برنامهٔ خریدار)</p>
      ) : (
        <p className="text-sm break-all">
          <bdi dir="ltr">{base}</bdi>
        </p>
      )}
    </SurfaceSection>
  );
}

function QrLinkDialog({ onClose }: { readonly onClose: () => void }) {
  const save = useNumberingSave('qr-link', onClose);
  const current = save.version?.qrLinkBase ?? null;
  const [typed, setTyped] = useState<string | undefined>(undefined);
  const base = typed ?? current ?? '';
  const [reason, setReason] = useState(defaultReason('editQrLink'));
  const [baseError, setBaseError] = useState<string | undefined>();
  const [reasonError, setReasonError] = useState<string | undefined>();

  function submit() {
    const trimmed = base.trim();
    const problem = qrLinkBaseProblem(trimmed);
    const noReason = reasonProblem(reason);
    setBaseError(problem);
    setReasonError(noReason);
    if (problem !== undefined || noReason !== undefined) return;
    void save.save({
      change: setQrLinkBase(trimmed === '' ? null : trimmed),
      fields: () => ({ qrLinkBase: 'qrLinkBase' }),
      reason,
      setError: (_field, message) => setBaseError(message),
    });
  }

  return (
    <ChangeDialog
      description="از کارت‌های بعدی و QR برنامهٔ خریدار با این آدرس ساخته می‌شود."
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      onSubmit={submit}
      open
      reason={
        <ReasonPicker
          error={reasonError}
          onChange={setReason}
          presets={REASON_PRESETS.editQrLink}
          value={reason}
        />
      }
      save={save}
      title="ویرایش لینک QR کارت"
    >
      <TextField
        error={baseError}
        hint={
          <>
            با <bdi dir="ltr">https://</bdi> شروع شود. برای آدرس پیش‌فرض خالی بگذارید.
          </>
        }
        inputMode="url"
        label="آدرس لینک"
        ltr
        maxLength={NUMBERING_BOUNDS.qrLinkBase}
        onChange={(event) => {
          setTyped(event.target.value);
          setBaseError(undefined);
        }}
        value={base}
      />
    </ChangeDialog>
  );
}
